import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Clock,
  Copy,
  KeyRound,
  ShieldCheck,
  ShieldOff,
  Trash2,
  UserPlus,
} from 'lucide-react';

import {
  createAuditorSeat,
  listAuditorSeats,
  revokeAuditorSeat,
  type AuditorSeat,
} from '@/api/auditorSeats.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useAuth } from '@/hooks/useAuth';

/**
 * Auditor-seat admin UI. Replaces the manual psql recipe documented in
 * `docs/deploy-staff-domain.md` and `docs/deploy-env-vars.md`.
 *
 * Visible only to visionary / integrator / quality_lead. Lower-privileged
 * compliance roles can still GET the list (so an inspector can confirm
 * their own seat) but the create / revoke buttons are gated behind the
 * `canManage` flag below — UI hint only; the server enforces the same
 * gate via `requireComplianceRole()`.
 *
 * Design intent: every operation is one click, with a required reason on
 * destructive actions (cGMP §111.180(c)). The full Part 11 audit trail is
 * still visible at /compliance/audit-log if anyone wants to verify what
 * happened after the fact.
 */
export function AuditorSeatsPage() {
  const { state: authState } = useAuth();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<AuditorSeat | null>(null);
  const [recentlyIssuedDevToken, setRecentlyIssuedDevToken] = useState<{
    email: string;
    devToken: string;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['compliance-auditor-seats'],
    queryFn: listAuditorSeats,
  });

  const canManage = useMemo(() => {
    if (authState.status !== 'authenticated') return false;
    return ['visionary', 'integrator', 'quality_lead'].includes(authState.me.membership.role);
  }, [authState]);

  const seats = data ?? [];
  const activeSeats = seats.filter((s) => s.status === 'active');
  const expiredSeats = seats.filter((s) => s.status === 'expired');

  function invalidateSeats(): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: ['compliance-auditor-seats'] });
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Link
            to="/compliance/sops"
            className="inline-flex items-center gap-1 text-[12px] text-kw-muted hover:text-kw-shark"
          >
            <ArrowLeft className="size-3.5" />
            Back to all SOPs
          </Link>
          <h1 className="typo-h1 mt-1">Auditor seats</h1>
          <p className="mt-1 text-[14px] text-kw-muted">
            Time-bounded read-only access for external inspectors. Each seat issues a magic-link credential
            scoped to your organization, with a Part 11 audit row on issue and revoke.
          </p>
        </div>
        {canManage ? (
          <Button
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            <UserPlus className="size-4" />
            Issue auditor seat
          </Button>
        ) : null}
      </div>

      {/* Dev-mode banner showing the magic-link token (real prod
          users won't see this — devToken is null when Stytch is live). */}
      {recentlyIssuedDevToken ? (
        <Card className="flex items-start gap-3 border-kw-warning-text/40 bg-kw-warning-bg p-4">
          <KeyRound className="mt-0.5 size-5 shrink-0 text-kw-warning-text" />
          <div className="flex-1">
            <p className="text-[14px] font-medium text-kw-warning-text">Dev-mode token issued</p>
            <p className="mt-1 text-[12px] text-kw-warning-text/80">
              Mock auth is on, so no email was sent. Share this verify URL with{' '}
              <strong>{recentlyIssuedDevToken.email}</strong>:
            </p>
            <DevTokenLink email={recentlyIssuedDevToken.email} devToken={recentlyIssuedDevToken.devToken} />
            <button
              type="button"
              onClick={() => {
                setRecentlyIssuedDevToken(null);
              }}
              className="mt-2 text-[11px] text-kw-warning-text/70 underline"
            >
              Dismiss
            </button>
          </div>
        </Card>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load seats</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : seats.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <ShieldCheck className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No auditor seats yet</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              Issue one when an inspector or external auditor needs read-only access.
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          <SeatSection
            title="Active"
            icon={<ShieldCheck className="size-4 text-kw-tag-text" />}
            seats={activeSeats}
            canManage={canManage}
            onRevoke={setRevokeTarget}
          />
          {expiredSeats.length > 0 ? (
            <SeatSection
              title="Expired"
              icon={<ShieldOff className="size-4 text-kw-muted" />}
              seats={expiredSeats}
              canManage={canManage}
              onRevoke={setRevokeTarget}
              dimmed
            />
          ) : null}
        </div>
      )}

      <CreateAuditorSeatModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
        }}
        onCreated={(devToken, email) => {
          if (devToken) setRecentlyIssuedDevToken({ devToken, email });
          void invalidateSeats();
        }}
      />

      <RevokeAuditorSeatModal
        seat={revokeTarget}
        onClose={() => {
          setRevokeTarget(null);
        }}
        onRevoked={() => {
          void invalidateSeats();
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Internals
// ─────────────────────────────────────────────────────────────────────────

function SeatSection({
  title,
  icon,
  seats,
  canManage,
  onRevoke,
  dimmed,
}: {
  title: string;
  icon: React.ReactNode;
  seats: AuditorSeat[];
  canManage: boolean;
  onRevoke: (seat: AuditorSeat) => void;
  dimmed?: boolean;
}) {
  if (seats.length === 0) return null;
  return (
    <section className="space-y-2">
      <h2 className="flex items-center gap-2 text-[13px] font-medium uppercase tracking-wide text-kw-muted">
        {icon}
        {title} <span className="font-normal text-kw-ghost">({seats.length})</span>
      </h2>
      <Card className={dimmed ? 'p-0 opacity-70' : 'p-0'}>
        <ul className="divide-y divide-kw-border">
          {seats.map((seat) => (
            <li key={seat.id} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-medium text-kw-shark">
                  {seat.firstName} {seat.lastName}
                </p>
                <p className="truncate text-[12px] text-kw-muted">{seat.email}</p>
              </div>
              <div className="flex flex-col items-start gap-1 sm:items-end">
                <Badge variant={seat.status === 'active' ? 'success' : 'neutral'}>
                  {seat.status === 'active' ? 'Active' : 'Expired'}
                </Badge>
                <p className="flex items-center gap-1 text-[11px] text-kw-muted">
                  <Clock className="size-3" />
                  {seat.expiresAt ? formatExpiry(seat.expiresAt) : 'No expiry'}
                </p>
              </div>
              {canManage ? (
                <button
                  type="button"
                  onClick={() => {
                    onRevoke(seat);
                  }}
                  aria-label={`Revoke seat for ${seat.email}`}
                  className="self-start text-[12px] text-kw-danger-text hover:underline sm:self-center"
                >
                  <Trash2 className="mr-1 inline size-3.5" />
                  Revoke
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}

function CreateAuditorSeatModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (devToken: string | null, email: string) => void;
}) {
  // Default to 14 days out — matches the doc'd inspector-seat default.
  const defaultExpiry = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  }, []);

  const [email, setEmail] = useState('');
  const [expiresAt, setExpiresAt] = useState(defaultExpiry);

  const mutation = useMutation({
    mutationFn: () =>
      createAuditorSeat({
        email: email.trim().toLowerCase(),
        // Send as YYYY-MM-DD; the server interprets that as local
        // end-of-day for the user-facing date, but Date.parse() lands at
        // 00:00 UTC. Add 1 day so a "Tuesday" expiry actually covers
        // Tuesday in any timezone west of UTC.
        expiresAt: addOneDay(expiresAt),
      }),
    onSuccess: (data) => {
      onCreated(data.devToken, data.user.email);
      onClose();
      setEmail('');
      setExpiresAt(defaultExpiry);
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!email.trim() || !expiresAt) return;
    mutation.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="Issue auditor seat" size="sm">
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        className="space-y-4"
      >
        <p className="text-[13px] text-kw-muted">
          Creates a read-only <strong>auditor_readonly</strong> membership scoped to your org. The inspector
          receives a magic-link email; their session JWT is auto-clamped so it can&apos;t outlive the seat.
        </p>

        <Input
          label="Inspector email"
          name="email"
          type="email"
          required
          autoComplete="off"
          placeholder="auditor.fda@example.gov"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
        />

        <Input
          label="Expires on"
          name="expiresAt"
          type="date"
          required
          value={expiresAt}
          onChange={(e) => {
            setExpiresAt(e.target.value);
          }}
          hint="Maximum 90-day window. Default is 14 days from today."
        />

        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">
              {mutation.error instanceof Error ? mutation.error.message : 'Could not issue seat'}
            </p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!email.trim() || !expiresAt}>
            Issue seat
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function RevokeAuditorSeatModal({
  seat,
  onClose,
  onRevoked,
}: {
  seat: AuditorSeat | null;
  onClose: () => void;
  onRevoked: () => void;
}) {
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () => {
      if (!seat) return Promise.resolve();
      return revokeAuditorSeat(seat.id, reason.trim());
    },
    onSuccess: () => {
      onRevoked();
      onClose();
      setReason('');
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!reason.trim()) return;
    mutation.mutate();
  }

  return (
    <Modal open={Boolean(seat)} onClose={onClose} title="Revoke auditor seat" size="sm">
      {seat ? (
        <form
          onSubmit={(e) => {
            handleSubmit(e);
          }}
          className="space-y-4"
        >
          <p className="text-[13px] text-kw-shark">
            Revoke access for{' '}
            <strong>
              {seat.firstName} {seat.lastName}
            </strong>{' '}
            (<span className="font-mono text-[12px]">{seat.email}</span>)?
          </p>
          <p className="text-[12px] text-kw-muted">
            Their session continues working until the JWT expires (worst-case the seat&apos;s expiry, often
            sooner once their browser re-fetches /api/auth/me).
          </p>
          <Input
            label="Reason for revoke"
            name="reasonForChange"
            required
            placeholder="e.g. audit walkthrough complete on 2026-05-12"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            hint="Required by cGMP §111.180(c). Captured in the audit log."
          />

          {mutation.error ? (
            <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
              <p className="text-[12px] text-kw-danger-text">
                {mutation.error instanceof Error ? mutation.error.message : 'Revoke failed'}
              </p>
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="danger" isLoading={mutation.isPending} disabled={!reason.trim()}>
              Revoke seat
            </Button>
          </div>
        </form>
      ) : null}
    </Modal>
  );
}

function DevTokenLink({ email, devToken }: { email: string; devToken: string }) {
  const url = `/auth/authenticate?token=${encodeURIComponent(devToken)}`;
  return (
    <div className="mt-2 flex items-center gap-2">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="block flex-1 break-all rounded bg-white px-2 py-1.5 font-mono text-[11px] text-kw-shark hover:bg-kw-bg"
      >
        {url}
      </a>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard.writeText(`${window.location.origin}${url}`);
        }}
        aria-label={`Copy verify link for ${email}`}
        className="rounded-kw-md border border-kw-warning-text/40 bg-white px-2 py-1.5 text-[11px] text-kw-warning-text hover:bg-kw-warning-bg"
      >
        <Copy className="size-3.5" />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

function addOneDay(yyyymmdd: string): string {
  const d = new Date(`${yyyymmdd}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString();
}

function formatExpiry(iso: string): string {
  const d = new Date(iso);
  const ms = d.getTime() - Date.now();
  if (ms <= 0) return `Expired ${d.toLocaleDateString()}`;
  const days = Math.ceil(ms / (24 * 60 * 60 * 1000));
  if (days === 1) return `Expires in 1 day · ${d.toLocaleDateString()}`;
  if (days <= 30) return `Expires in ${String(days)} days · ${d.toLocaleDateString()}`;
  return `Expires ${d.toLocaleDateString()}`;
}
